import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../../services/api';
import {
  archiveScrapeProfile,
  createScrapeProfile,
  getScrapeProfiles,
  getScrapeRun,
  getScrapeRuns,
  restoreScrapeProfile,
  runScrapeProfile,
  updateScrapeProfile,
} from '../../../services/scrape-profiles';
import type {
  ScrapeProfile,
  ScrapeProfileRequest,
  ScrapeRunDetail,
  ScrapeRunSummary,
} from '../../../types/scraping';
import { NEW_PROFILE_DEFAULTS } from '../../../types/scraping';
import { AdminPage, AdminPageHeader, AdminPanel } from '../AdminPage';
import { ScrapeProfileForm } from './ScrapeProfileForm';
import { ScrapeRunInspector } from './ScrapeRunInspector';
import { formatRunLabel } from './runDiagnostics';
import './ScrapingSection.css';

/** Sentinel for "the unsaved new profile" so it can share the selection state with real ids. */
const NEW_PROFILE_ID = '__new__';

/** Turns a saved profile into the form's editable shape. */
const toRequest = (profile: ScrapeProfile): ScrapeProfileRequest => ({
  name: profile.name,
  description: profile.description ?? '',
  url: profile.url,
  waitUntil: profile.waitUntil,
  waitForSelector: profile.waitForSelector ?? '',
  waitMilliseconds: profile.waitMilliseconds,
  timeoutSeconds: profile.timeoutSeconds,
  viewportWidth: profile.viewportWidth,
  viewportHeight: profile.viewportHeight,
  userAgent: profile.userAgent ?? '',
  nextPageSelector: profile.nextPageSelector ?? '',
  maxPages: profile.maxPages,
  checkRobots: profile.checkRobots,
  extractionPrompt: profile.extractionPrompt,
});

/**
 * The scraping tool's admin surface: pick a profile, edit its fetch config and prompt, run it, and read
 * the result next to the page it came from.
 *
 * The layout follows the loop this exists to support — tweak the prompt, re-run against the same URL,
 * compare with the previous run — so the run history sits beside the editor rather than on another page.
 */
export const ScrapingSection = () => {
  const [profiles, setProfiles] = useState<ScrapeProfile[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ScrapeProfileRequest | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  const [runs, setRuns] = useState<ScrapeRunSummary[]>([]);
  const [activeRun, setActiveRun] = useState<ScrapeRunDetail | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /**
   * Aborts in-flight work when the selection changes or the component unmounts. A run can take minutes,
   * so without this a developer switching profiles mid-run would get the old run's result written over
   * the new selection.
   */
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const selectedProfile = useMemo(
    () => profiles.find((p) => p.id === selectedId) ?? null,
    [profiles, selectedId]
  );

  const isNew = selectedId === NEW_PROFILE_ID;

  /**
   * Whether the editor holds unsaved changes. Declared before the handlers that read it — Run refuses
   * while dirty, so that a run can never reflect a different prompt than the one on screen.
   */
  const isDirty = useMemo(() => {
    if (!draft) return false;
    if (isNew) return true;
    if (!selectedProfile) return false;
    return JSON.stringify(draft) !== JSON.stringify(toRequest(selectedProfile));
  }, [draft, isNew, selectedProfile]);

  /**
   * Builds the banner text. The API's own message wins when there is one — it is specific and
   * actionable ("A scrape profile named X already exists"). For anything else the CALLER's context
   * leads, with the technical detail appended: a bare "Request timed out" is much less useful than
   * "the run may still have completed — check the run history", and dropping that guidance in favour
   * of the transport message is how a developer ends up thinking a finished run was lost.
   */
  const describeError = (err: unknown, fallback: string): string => {
    if (err instanceof ApiError) {
      const body = err.response as { message?: string } | undefined;
      if (body?.message) return body.message;
      return `${fallback} (HTTP ${err.statusCode})`;
    }

    const detail = err instanceof Error ? err.message : null;
    return detail ? `${fallback} (${detail})` : fallback;
  };

  const loadProfiles = useCallback(async (withArchived: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const list = await getScrapeProfiles(withArchived);
      setProfiles(list);
      return list;
    } catch (err) {
      setError(describeError(err, 'Could not load scrape profiles.'));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProfiles(includeArchived);
  }, [loadProfiles, includeArchived]);

  const loadRuns = useCallback(async (profileId: string) => {
    try {
      setRuns(await getScrapeRuns(profileId));
    } catch (err) {
      setError(describeError(err, 'Could not load run history.'));
    }
  }, []);

  const handleSelect = async (profile: ScrapeProfile) => {
    abortRef.current?.abort();
    abortRef.current = null;

    setSelectedId(profile.id);
    setDraft(toRequest(profile));
    setActiveRun(null);
    setRuns([]);
    setError(null);
    setNotice(null);
    await loadRuns(profile.id);
  };

  const handleNew = () => {
    abortRef.current?.abort();
    abortRef.current = null;

    setSelectedId(NEW_PROFILE_ID);
    setDraft({ ...NEW_PROFILE_DEFAULTS });
    setActiveRun(null);
    setRuns([]);
    setError(null);
    setNotice(null);
  };

  const handleSave = async () => {
    if (!draft) return;

    if (!draft.name.trim() || !draft.url.trim() || !draft.extractionPrompt.trim()) {
      setError('Name, URL, and an extraction prompt are all required.');
      return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saved = isNew
        ? await createScrapeProfile(draft)
        : await updateScrapeProfile(selectedId!, draft);

      const list = await loadProfiles(includeArchived);
      // Prefer the freshly listed row so the sidebar and the editor cannot disagree.
      const refreshed = list.find((p) => p.id === saved.id) ?? saved;

      setSelectedId(refreshed.id);
      setDraft(toRequest(refreshed));
      setNotice(isNew ? 'Profile created.' : 'Profile saved.');

      if (isNew) {
        await loadRuns(refreshed.id);
      }
    } catch (err) {
      setError(describeError(err, 'Could not save the profile.'));
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    if (!selectedProfile) return;

    // Saving first means the run always reflects what is on screen — otherwise a developer would tweak
    // the prompt, hit Run, and get a result from the previously saved version.
    if (isDirty) {
      setError('Save your changes before running, so the run matches what you see.');
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setRunning(true);
    setError(null);
    setNotice(null);
    try {
      const run = await runScrapeProfile(selectedProfile.id, controller.signal);
      setActiveRun(run);
      await loadRuns(selectedProfile.id);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(
        describeError(
          err,
          'The run did not come back. It may still have completed — check the run history below.'
        )
      );
      // The server persists the run row before starting work, so refresh the history: the outcome is
      // very often sitting there even when the request itself timed out.
      await loadRuns(selectedProfile.id);
    } finally {
      if (!controller.signal.aborted) setRunning(false);
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  const handleOpenRun = async (runId: string) => {
    setError(null);
    try {
      setActiveRun(await getScrapeRun(runId));
    } catch (err) {
      setError(describeError(err, 'Could not load that run.'));
    }
  };

  const handleToggleArchive = async () => {
    if (!selectedProfile) return;

    setSaving(true);
    setError(null);
    try {
      const updated = selectedProfile.isArchived
        ? await restoreScrapeProfile(selectedProfile.id)
        : await archiveScrapeProfile(selectedProfile.id);

      setNotice(updated.isArchived ? 'Profile archived.' : 'Profile restored.');

      const list = await loadProfiles(includeArchived);
      // An archived profile drops out of the default list, so the editor must let go of it too.
      if (!list.some((p) => p.id === updated.id)) {
        setSelectedId(null);
        setDraft(null);
        setRuns([]);
        setActiveRun(null);
      }
    } catch (err) {
      setError(describeError(err, 'Could not change the archive state.'));
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || running;

  return (
    <AdminPage width="full" className="scraping">
      <AdminPageHeader
        title="Web Scraping"
        subtitle={
          <>
            Define a scrape profile, run it against a public URL, and inspect what came back. Nothing
            here writes into RosterPilot — this is for proving a source can be read.
          </>
        }
        actions={
          <button type="button" className="scraping__new-btn" onClick={handleNew} disabled={busy}>
            New profile
          </button>
        }
      />

      {error && (
        <div className="admin-error" role="alert">
          {error}
        </div>
      )}
      {notice && <div className="scraping__notice">{notice}</div>}

      <div className="scraping__layout">
        <aside className="scraping__sidebar">
          <label className="scraping__archived-toggle">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Show archived
          </label>

          {loading && <p className="admin-state">Loading…</p>}
          {!loading && profiles.length === 0 && (
            <p className="admin-state">
              No profiles yet. Create one to point at a schedule page.
            </p>
          )}

          <ul className="scraping__profile-list">
            {isNew && (
              <li>
                {/* Not a button: the unsaved draft is already the active selection, so there is
                    nothing to navigate to. */}
                <div
                  className="scraping__profile scraping__profile--active"
                  aria-current="true"
                >
                  <span className="scraping__profile-name">New profile (unsaved)</span>
                </div>
              </li>
            )}
            {profiles.map((profile) => (
              <li key={profile.id}>
                <button
                  type="button"
                  className={`scraping__profile ${
                    profile.id === selectedId ? 'scraping__profile--active' : ''
                  }`}
                  onClick={() => void handleSelect(profile)}
                  disabled={busy}
                >
                  <span className="scraping__profile-name">
                    {profile.name}
                    {profile.isArchived && (
                      <span className="scraping__profile-archived">archived</span>
                    )}
                  </span>
                  <span className="scraping__profile-url">{profile.url}</span>
                  {profile.latestRun && (
                    <span
                      className={`scraping__profile-status scraping__profile-status--${profile.latestRun.status.toLowerCase()}`}
                    >
                      last run: {formatRunLabel(profile.latestRun)}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <div className="scraping__main">
          {!draft && (
            <p className="admin-state scraping__placeholder">
              Select a profile, or create one, to get started.
            </p>
          )}

          {draft && (
            <>
              <ScrapeProfileForm value={draft} onChange={setDraft} disabled={busy} />

              <div className="scraping__actions">
                <button
                  type="button"
                  className="scraping__save-btn"
                  onClick={() => void handleSave()}
                  disabled={busy || !isDirty}
                >
                  {saving ? 'Saving…' : isNew ? 'Create profile' : 'Save changes'}
                </button>

                {!isNew && (
                  <>
                    <button
                      type="button"
                      className="scraping__run-btn"
                      onClick={() => void handleRun()}
                      disabled={busy || isDirty || selectedProfile?.isArchived}
                      title={
                        isDirty
                          ? 'Save your changes first so the run matches what you see'
                          : selectedProfile?.isArchived
                            ? 'Restore this profile before running it'
                            : undefined
                      }
                    >
                      {running ? 'Running…' : 'Run now'}
                    </button>

                    <button
                      type="button"
                      className="scraping__archive-btn"
                      onClick={() => void handleToggleArchive()}
                      disabled={busy}
                    >
                      {selectedProfile?.isArchived ? 'Restore' : 'Archive'}
                    </button>
                  </>
                )}
              </div>

              {running && (
                <p className="scraping__muted">
                  Rendering in headless Chromium, then extracting. This can take a minute or two on a
                  slow site — the run is saved as it goes, so it will appear in the history either way.
                </p>
              )}

              {activeRun && <ScrapeRunInspector run={activeRun} />}

              {!isNew && runs.length > 0 && (
                <AdminPanel
                  title="Run history"
                  description={
                    <>
                      Every run is kept so prompt changes can be compared. Page content is retained for
                      the most recent runs only.
                    </>
                  }
                >
                  <ul className="scraping__run-list">
                    {runs.map((run) => (
                      <li key={run.id}>
                        <button
                          type="button"
                          className={`scraping__run ${
                            run.id === activeRun?.id ? 'scraping__run--active' : ''
                          }`}
                          onClick={() => void handleOpenRun(run.id)}
                        >
                          <span
                            className={`scraping__run-dot scraping__run-dot--${run.status.toLowerCase()}`}
                            aria-hidden="true"
                          />
                          <span className="scraping__run-label">{formatRunLabel(run)}</span>
                          {run.rawContentPruned && (
                            <span className="scraping__run-pruned">content pruned</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </AdminPanel>
              )}
            </>
          )}
        </div>
      </div>
    </AdminPage>
  );
};
